import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Ban, Download, Loader2, Plus, Receipt, RefreshCw, Search, Wallet } from "lucide-react";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { ApiService } from "../../services/api";
import checkPermission, { checkSuperAdmin } from "../../utils/permission";
import { useModal } from "../../hooks/useModal";
import { PaymentOrder, PaymentSubscription } from "../../types/payment";
import GrantPackageModal from "./components/GrantPackageModal";
import InvoiceDetailModal from "./components/InvoiceDetailModal";
import {
  getPackageLabel,
  PAYMENT_SOURCE_LABELS,
  ORDER_STATUS_LABELS,
  ORDER_STATUS_CLASSES,
  formatMoney,
  formatDateTime,
  formatRemaining,
  fullName,
} from "./paymentFormat";

const MANUAL_PROVIDERS = ["cash", "free"];

function SourceBadge({ source }: { source: string }) {
  const manual = MANUAL_PROVIDERS.includes(source);
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        source === "free" ? "bg-violet-50 text-violet-700" : manual ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"
      }`}
    >
      {PAYMENT_SOURCE_LABELS[source] || source}
    </span>
  );
}

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (value: string) => void; placeholder: string }) {
  return (
    <div className="relative w-full max-w-xs">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
      />
    </div>
  );
}

/** Aktif Paketler: erişimi açık olan kişiler, ne zaman bitiyor, hangi kaynakla açıldı. */
function SubscriptionsTab({ onGrant, refreshKey }: { onGrant: () => void; refreshKey: number }) {
  const { showToast, showConfirm } = useModal();
  const canManage = checkSuperAdmin() || checkPermission("payment_grant_manage");
  const [items, setItems] = useState<PaymentSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setLoading(true);
    ApiService.getPaymentSubscriptions({ search: search.trim() || undefined })
      .then((data) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [search, reload, refreshKey]);

  const handleEnd = async (item: PaymentSubscription) => {
    const confirmed = await showConfirm({
      title: "Erişimi sonlandır",
      message: `${fullName(item.user) || "Kullanıcı"} için bu paket (${getPackageLabel(item.paymentOrder) || item.reason || "erişim"}) hemen kapatılacak. Kişinin varsa diğer paketleri sürer. Onaylıyor musunuz?`,
      confirmButtonVariant: "danger",
    });
    if (!confirmed) return;
    try {
      await ApiService.endPaymentSubscription(item.userId, item.id);
      showToast("Erişim sonlandırıldı.", "success");
      setReload((prev) => prev + 1);
    } catch (error: any) {
      showToast(error?.message || "Erişim sonlandırılamadı.", "error");
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <SearchBox value={search} onChange={setSearch} placeholder="Ad, soyad veya e-posta" />
        <div className="flex items-center gap-2">
          <button
            onClick={() => setReload((prev) => prev + 1)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" />
            Yenile
          </button>
          {canManage && (
            <button
              onClick={onGrant}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Paket Tanımla
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
          Şu anda aktif paketi olan kullanıcı yok.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Kullanıcı</th>
                <th className="px-4 py-3">Paket</th>
                <th className="px-4 py-3">Kaynak</th>
                <th className="px-4 py-3">Başlangıç</th>
                <th className="px-4 py-3">Bitiş</th>
                <th className="px-4 py-3">Kalan</th>
                {canManage && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{fullName(item.user) || item.userId}</div>
                    <div className="text-xs text-slate-400">{item.user?.email}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {getPackageLabel(item.paymentOrder) || item.reason || "-"}
                    <div className="text-xs text-slate-400">
                      {item.modules ? item.modules.map((module) => module.label).join(", ") : "Tüm ücretli bölümler"}
                      {item.qrExamDaysOnly && " · salon yalnız deneme günleri"}
                      {item.entries && ` · ${item.entries.remaining}/${item.entries.total} giriş kaldı`}
                    </div>
                  </td>
                  <td className="px-4 py-3"><SourceBadge source={item.source} /></td>
                  <td className="px-4 py-3 text-slate-600">{formatDateTime(item.startsAt)}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">{formatDateTime(item.endsAt)}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${item.remainingMs < 86400000 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
                      {formatRemaining(item.remainingMs)}
                    </span>
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleEnd(item)}
                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                        title="Erişimi sonlandır"
                      >
                        <Ban className="h-3.5 w-3.5" />
                        Sonlandır
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Tüm Ödemeler: kart ödemeleri + panelden tanımlanan nakit/ücretsiz paketler tek listede. */
function OrdersTab({ onGrant, refreshKey }: { onGrant: () => void; refreshKey: number }) {
  const { showToast, showConfirm } = useModal();
  const canManage = checkSuperAdmin() || checkPermission("payment_grant_manage");
  const [orders, setOrders] = useState<PaymentOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [source, setSource] = useState<"" | "card" | "manual">("");
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState<PaymentOrder | null>(null);
  const [exporting, setExporting] = useState(false);

  // Excel de aynı filtreleri kullanır: ekranda ne görünüyorsa dosyaya o iner.
  const filters = useMemo(() => ({
    search: search.trim() || undefined,
    source: source || undefined,
    status: status || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  }), [search, source, status, startDate, endDate]);

  useEffect(() => {
    setLoading(true);
    ApiService.getPaymentOrders({ ...filters, limit: 250 })
      .then((data) => setOrders((data as PaymentOrder[]) || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [filters, reload, refreshKey]);

  // Paraşüt "Müşteri ve Tedarikçiler" şablonu olarak indirir (dosyayı backend hazırlar).
  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await ApiService.exportPaymentOrders(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `parasut-musteri-tedarikci-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      showToast(error?.message || "Excel indirilemedi.", "error");
    } finally {
      setExporting(false);
    }
  };

  const totals = useMemo(() => {
    const paid = orders.filter((order) => order.status === "PAID");
    const sum = (list: PaymentOrder[]) => list.reduce((acc, order) => acc + Number(order.paidAmount || 0), 0);
    return {
      count: paid.length,
      total: sum(paid),
      cash: sum(paid.filter((order) => order.provider === "cash")),
      card: sum(paid.filter((order) => !MANUAL_PROVIDERS.includes(order.provider))),
    };
  }, [orders]);

  // Callback kaybolduysa (müşteri 3DS sonrası pencereyi kapattı vb.) sipariş
  // "Bekliyor"da takılır ama para sağlayıcıya geçmiş olabilir — sorgulayıp düzelt.
  const [reconciling, setReconciling] = useState<string | null>(null);
  const handleReconcile = async (order: PaymentOrder) => {
    setReconciling(order.id);
    try {
      const updated = await ApiService.reconcilePaymentOrder(order.id);
      // Sağlayıcının kendi gerekçesi varsa onu göster — "sonuçlanmış ödeme yok" demek
      // yöneticiye hiçbir şey anlatmıyordu (asıl sebep ör. "Son kullanma tarihi hatalı").
      const info = updated?.reconcile;
      const gerekce = [info?.providerMessage, info?.providerCode ? `(kod ${info.providerCode})` : ""]
        .filter(Boolean).join(" ");
      if (updated?.status === "PAID") {
        showToast("Ödeme sağlayıcıdan doğrulandı, erişim açıldı.", "success");
      } else if (info?.action === "pending_fresh") {
        showToast(
          `Sağlayıcı bu ödemeyi henüz onaylamadı${gerekce ? `: ${gerekce}` : ""}. Sipariş 30 dakikadan yeni olduğu için başarısız işaretlenmedi — müşteri hâlâ ödeme ekranında olabilir.`,
          "info"
        );
      } else if (updated?.status === "FAILED") {
        showToast(
          gerekce
            ? `Sağlayıcı ödemeyi doğrulamadı: ${gerekce}. Sipariş başarısız olarak işaretlendi.`
            : "Sağlayıcı ödemeyi doğrulamadı; sipariş başarısız olarak işaretlendi.",
          "error"
        );
      } else if (updated?.status === "REFUNDED") {
        showToast("Sağlayıcı işlemin artık aktif olmadığını bildirdi; erişim geri alındı.", "error");
      } else {
        showToast(`Sağlayıcıda sonuçlanmış bir ödeme görünmüyor${gerekce ? `: ${gerekce}` : ""}.`, "info");
      }
      setReload((prev) => prev + 1);
    } catch (error: any) {
      showToast(error?.message || "Sağlayıcı sorgusu yapılamadı.", "error");
    } finally {
      setReconciling(null);
    }
  };

  const handleCancel = async (order: PaymentOrder) => {
    const confirmed = await showConfirm({
      title: "Paketi iptal et",
      message: "Bu paketin eklediği süre geri alınacak. Onaylıyor musunuz?",
      confirmButtonVariant: "danger",
    });
    if (!confirmed) return;
    try {
      await ApiService.cancelPaymentGrant(order.id);
      showToast("Paket iptal edildi.", "success");
      setReload((prev) => prev + 1);
    } catch (error: any) {
      showToast(error?.message || "Paket iptal edilemedi.", "error");
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-medium text-slate-500">Toplam Tahsilat</div>
          <div className="mt-1 text-lg font-bold text-slate-900">{formatMoney(totals.total)}</div>
          <div className="text-[11px] text-slate-400">{totals.count} ödeme</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-medium text-slate-500">Kart ile</div>
          <div className="mt-1 text-lg font-bold text-slate-900">{formatMoney(totals.card)}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-medium text-slate-500">Nakit</div>
          <div className="mt-1 text-lg font-bold text-slate-900">{formatMoney(totals.cash)}</div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SearchBox value={search} onChange={setSearch} placeholder="Ad, soyad veya e-posta" />
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as "" | "card" | "manual")}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none"
          >
            <option value="">Tüm kaynaklar</option>
            <option value="card">Kart ödemeleri</option>
            <option value="manual">Nakit / Ücretsiz</option>
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none"
          >
            <option value="">Tüm durumlar</option>
            <option value="PAID">Ödendi</option>
            <option value="PENDING">Bekliyor</option>
            <option value="FAILED">Başarısız</option>
            <option value="CANCELLED">İptal</option>
          </select>
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => setStartDate(e.target.value)}
              title="Başlangıç tarihi"
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none"
            />
            <span className="text-slate-400">–</span>
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              title="Bitiş tarihi"
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none"
            />
            {(startDate || endDate) && (
              <button
                onClick={() => { setStartDate(""); setEndDate(""); }}
                className="rounded-lg px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100"
              >
                Temizle
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={exporting || orders.length === 0}
            title="Filtrelenen kayıtları Paraşüt müşteri/tedarikçi şablonu olarak indir"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Paraşüt Excel
          </button>
          <button
            onClick={() => setReload((prev) => prev + 1)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" />
            Yenile
          </button>
          {canManage && (
            <button
              onClick={onGrant}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Paket Tanımla
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : orders.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
          Kayıt bulunamadı.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full min-w-[880px] text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Kullanıcı</th>
                <th className="px-4 py-3">Kaynak</th>
                <th className="px-4 py-3">Paket</th>
                <th className="px-4 py-3">Tutar</th>
                <th className="px-4 py-3">Durum</th>
                <th className="px-4 py-3">Tarih</th>
                <th className="px-4 py-3">Fatura</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{fullName(order.user) || order.userId}</div>
                    <div className="text-xs text-slate-400">{order.user?.email}</div>
                  </td>
                  <td className="px-4 py-3"><SourceBadge source={order.provider} /></td>
                  <td className="px-4 py-3 text-slate-700">{getPackageLabel(order)}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">{formatMoney(order.paidAmount)}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ORDER_STATUS_CLASSES[order.status] || "bg-slate-100 text-slate-600"}`}>
                      {ORDER_STATUS_LABELS[order.status] || order.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatDateTime(order.paidAt || order.createdAt)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelected(order)}
                      className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50"
                    >
                      <Receipt className="h-3.5 w-3.5" />
                      {order.billingInfo?.address ? "Fatura" : "Detay"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!MANUAL_PROVIDERS.includes(order.provider) && ["PENDING", "FAILED"].includes(order.status) && (
                      <button
                        onClick={() => handleReconcile(order)}
                        disabled={reconciling === order.id}
                        title="Ödemeyi sağlayıcıdan sorgula — kaybolan callback'i veya yanlış 'Başarısız' hükmünü düzeltir"
                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${reconciling === order.id ? "animate-spin" : ""}`} />
                        Sorgula
                      </button>
                    )}
                    {canManage && MANUAL_PROVIDERS.includes(order.provider) && order.status === "PAID" && (
                      <button
                        onClick={() => handleCancel(order)}
                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        <Ban className="h-3.5 w-3.5" />
                        İptal
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InvoiceDetailModal order={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

const REFERRAL_STATE_LABELS: Record<string, string> = {
  WAITING: "Cayma süresinde",
  ELIGIBLE: "Ödül verilebilir",
  REWARDED: "Ödül verildi",
  CANCELLED: "İptal (iade)",
};

const REFERRAL_STATE_CLASSES: Record<string, string> = {
  WAITING: "bg-amber-50 text-amber-700",
  ELIGIBLE: "bg-emerald-50 text-emerald-700",
  REWARDED: "bg-slate-100 text-slate-600",
  CANCELLED: "bg-red-50 text-red-600",
};

/** Davetler: Arkadaşını Getir satışları, getirene verilecek ödüller ve davet linklerinin dışa aktarımı. */
function ReferralsTab({ refreshKey }: { refreshKey: number }) {
  const { showToast } = useModal();
  const canManage = checkSuperAdmin() || checkPermission("payment_grant_manage");
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [reload, setReload] = useState(0);
  const [rewarding, setRewarding] = useState<{ id: string; note: string } | null>(null);
  const [exportRange, setExportRange] = useState({ from: "", to: "" });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setLoading(true);
    ApiService.getPaymentReferrals({ state: stateFilter || undefined, search: search.trim() || undefined })
      .then((data) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [search, stateFilter, reload, refreshKey]);

  const saveReward = async () => {
    if (!rewarding) return;
    try {
      await ApiService.rewardPaymentReferral(rewarding.id, rewarding.note);
      showToast("Ödül verildi olarak işaretlendi.", "success");
      setRewarding(null);
      setReload((prev) => prev + 1);
    } catch (error: any) {
      showToast(error?.message || "Kaydedilemedi.", "error");
    }
  };

  const downloadInviteLinks = async () => {
    setExporting(true);
    try {
      const blob = await ApiService.exportPaymentInviteLinks({
        origin: window.location.origin,
        registeredFrom: exportRange.from || undefined,
        registeredTo: exportRange.to || undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `davet-linkleri-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      showToast(error?.message || "Excel indirilemedi.", "error");
    } finally {
      setExporting(false);
    }
  };

  const eligibleCount = items.filter((item) => item.state === "ELIGIBLE").length;

  return (
    <div className="p-4">
      {canManage && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-sm font-semibold text-slate-800">Davet linklerini indir</p>
          <p className="mt-0.5 text-xs text-slate-500">
            Ücretli departmanlardaki öğrencilerin kişisel davet linkleri ve hazır mesaj Excel olarak iner; "WhatsApp'ta aç" sütunu mesajı
            o kişiye açar. Kayıt tarihiyle süzebilirsin (ör. bu sezon kayıt olanlar). Toplu ticari mesajda kişinin ileti izni gerekir.
          </p>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <label className="text-xs text-slate-500">
              <span className="mb-1 block font-semibold">Kayıt başlangıç</span>
              <input type="date" value={exportRange.from} onChange={(e) => setExportRange({ ...exportRange, from: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm" />
            </label>
            <label className="text-xs text-slate-500">
              <span className="mb-1 block font-semibold">Kayıt bitiş</span>
              <input type="date" value={exportRange.to} onChange={(e) => setExportRange({ ...exportRange, to: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm" />
            </label>
            <button
              onClick={downloadInviteLinks}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Excel indir
            </button>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-72 max-w-full">
            <SearchBox value={search} onChange={setSearch} placeholder="Getiren ya da gelen" />
          </div>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          >
            <option value="">Tüm durumlar</option>
            {Object.entries(REFERRAL_STATE_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </div>
        {eligibleCount > 0 && (
          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">{eligibleCount} ödül verilmeyi bekliyor</span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
          Henüz davetle yapılmış satış yok.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Getiren</th>
                <th className="px-4 py-3">Gelen</th>
                <th className="px-4 py-3">Paket</th>
                <th className="px-4 py-3">İndirim</th>
                <th className="px-4 py-3">Getirene ödül</th>
                <th className="px-4 py-3">Durum</th>
                {canManage && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-slate-100 align-top last:border-0">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{fullName(item.referrer) || item.referrerUserId}</div>
                    <div className="text-xs text-slate-400">{item.referrer?.phone || item.referrer?.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{fullName(item.referred) || item.referredUserId}</div>
                    <div className="text-xs text-slate-400">{formatDateTime(item.createdAt)}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{item.packageLabel || "-"}</td>
                  <td className="px-4 py-3 text-slate-700">{formatMoney(item.discountAmount)}</td>
                  <td className="px-4 py-3 text-slate-700">{item.referrerReward || "-"}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${REFERRAL_STATE_CLASSES[item.state] || ""}`}>
                      {REFERRAL_STATE_LABELS[item.state] || item.state}
                    </span>
                    {item.state === "WAITING" && (
                      <div className="mt-1 text-xs text-slate-400">{formatDateTime(item.eligibleAt)} sonrası</div>
                    )}
                    {item.state === "REWARDED" && (
                      <div className="mt-1 text-xs text-slate-400">{formatDateTime(item.rewardedAt)}{item.rewardNote ? ` · ${item.rewardNote}` : ""}</div>
                    )}
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right">
                      {item.state === "ELIGIBLE" && rewarding?.id !== item.id && (
                        <button
                          onClick={() => setRewarding({ id: item.id, note: "" })}
                          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                        >
                          Ödül verildi
                        </button>
                      )}
                      {rewarding && rewarding.id === item.id && (
                        <div className="flex items-center justify-end gap-1.5">
                          <input
                            autoFocus
                            value={rewarding.note}
                            maxLength={200}
                            placeholder="Not (ör. Boyner çeki no)"
                            onChange={(e) => setRewarding((prev) => (prev ? { ...prev, note: e.target.value } : prev))}
                            className="w-48 rounded-lg border border-slate-200 px-2 py-1 text-xs"
                          />
                          <button onClick={saveReward} className="rounded-lg bg-emerald-600 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-700">Kaydet</button>
                          <button onClick={() => setRewarding(null)} className="rounded-lg px-2 py-1 text-xs text-slate-500 hover:bg-slate-100">Vazgeç</button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function PaymentsAdmin() {
  const canView = checkSuperAdmin() || checkPermission("payment_orders_view");
  const [grantOpen, setGrantOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const openGrant = useCallback(() => setGrantOpen(true), []);
  const handleGranted = useCallback(() => setRefreshKey((prev) => prev + 1), []);

  const tabs: TabItemProps[] = useMemo(() => ([
    {
      type: "custom",
      title: "Aktif Paketler",
      path: "subscriptions",
      customComponent: <SubscriptionsTab onGrant={openGrant} refreshKey={refreshKey} />,
    },
    {
      type: "custom",
      title: "Tüm Ödemeler",
      path: "orders",
      customComponent: <OrdersTab onGrant={openGrant} refreshKey={refreshKey} />,
    },
    {
      type: "custom",
      title: "Davetler",
      path: "referrals",
      customComponent: <ReferralsTab refreshKey={refreshKey} />,
    },
  ]), [openGrant, refreshKey]);

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-slate-500">
        <div>
          <Wallet className="mx-auto mb-3 h-8 w-8 text-slate-300" />
          Bu sayfayı görüntüleme yetkiniz yok.
        </div>
      </div>
    );
  }

  return (
    <>
      <TabPage title="Ödemeler" tabs={tabs} />
      <GrantPackageModal open={grantOpen} onClose={() => setGrantOpen(false)} onSuccess={handleGranted} />
    </>
  );
}
